import { useEffect, useRef, useState } from "react";
import { eq, useLiveQuery } from "@tanstack/react-db";
import { LuArrowLeft, LuCamera, LuFlag, LuPencil, LuRotateCcw, LuTrash2 } from "react-icons/lu";
import { Heading, Muted, Small } from "@/components/typography";
import { Alert, AlertAction, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Else, If } from "@/components/ui/conditional";
import { Progress } from "@/components/ui/progress";
import { scriptsCollection } from "@/lib/collections/scripts-collection";
import { useScriptPages } from "@/lib/collections/use-script-pages";
import { blobStore } from "@/lib/blob-store";
import { isRemovable, removeRefuseReason } from "@/lib/page-actions";
import useToggle from "@/hooks/state/useToggle";
import type { Page } from "@/lib/page";
import type { AddPageOptions } from "@/lib/page-persist";
import type { Script } from "@/lib/script";
import { displayLabel } from "@/lib/script-summary";
import { Lightbox } from "@/components/ui/lightbox";
import { useQueueBannerText } from "@/lib/queue-banner";
import { pageStatusBreakdown, blockedPageNotice, failReasonLabel, pageStatusLabel } from "@/lib/page-status";
import { useAckFlag } from "../hooks/use-ack-flag";
import { useFlagState } from "../lib/flag-message-store";
import { addPageFromBlob, deletePage, deleteScriptAndPages } from "../hooks/use-scripts";
import { NativeCameraButton } from "./native-camera-button";
import { ResolveFlagDialog } from "./resolve-flag-dialog";
import { UploadNowButton } from "./upload-now";
import { ScriptFormDrawer } from "./script-form-drawer";

export function ScriptDetailPage({
  scriptId,
  onBack,
}: {
  scriptId: string;
  onBack: () => void;
}) {
  const { data: scripts } = useLiveQuery({
    query: q => q.from({ script: scriptsCollection }).where(({ script }) => eq(script.id, scriptId)),
  });
  const pages = useScriptPages(scriptId);
  const [thumbs, setThumbs] = useState<Record<string, string>>({});
  const thumbsRef = useRef(thumbs);
  thumbsRef.current = thumbs;
  const [notice, setNotice] = useState<string | null>(null);
  const [editing, { setRight: openEdit, setLeft: closeEdit }] = useToggle();
  const [lightboxPageId, setLightboxPageId] = useState<string | null>(null);
  const banner = useQueueBannerText(pages.some(p => p.uploadStatus !== "uploaded"));
  const flag = useFlagState(scriptId);
  const { ack, pending } = useAckFlag();
  const [resolveOpen, setResolveOpen] = useState(false);

  useEffect(() => {
    return () => {
      for (const url of Object.values(thumbsRef.current)) URL.revokeObjectURL(url);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const entries: Record<string, string> = {};
      for (const page of pages) {
        if (thumbs[page.id]) continue;
        const blob = await blobStore.get(page.blobKey);
        if (blob) entries[page.id] = URL.createObjectURL(blob);
      }
      if (cancelled) {
        for (const url of Object.values(entries)) URL.revokeObjectURL(url);
        return;
      }
      if (Object.keys(entries).length) {
        setThumbs(prev => ({ ...prev, ...entries }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pages]);

  const script = scripts[0];

  /** A reviewer flag reaches the phone via the 5s poll (flag-message-store).
   * Script stays in Home Flagged until approve; ack only silences the nudge.
   * Skip while resolve dialog is open or an ack is in flight so a silent
   * null-reply post cannot overwrite an explicit resolve note. */
  useEffect(() => {
    if (flag && !flag.ackedAt && script?.remoteId && !pending && !resolveOpen) {
      void ack(scriptId, script.remoteId).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flag?.ackedAt, flag?.message, script?.remoteId, scriptId, pending, resolveOpen]);

  async function handleResolve(reply: string) {
    if (!script?.remoteId) return;
    try {
      await ack(scriptId, script.remoteId, { reply, optimistic: false });
      setResolveOpen(false);
    } catch {
      // toast already shown in useAckFlag
    }
  }

  const uploadedCount = pages.filter(p => p.uploadStatus === "uploaded").length;
  const lightboxPage = pages.find(p => p.id === lightboxPageId) ?? null;

  async function removePage(page: Page): Promise<boolean> {
    const reason = removeRefuseReason(page.uploadStatus);
    if (reason) {
      setNotice(reason);
      return false;
    }
    const thumb = thumbs[page.id];
    if (thumb) URL.revokeObjectURL(thumb);
    setThumbs(prev => {
      const next = { ...prev };
      delete next[page.id];
      return next;
    });
    setLightboxPageId(current => (current === page.id ? null : current));
    await deletePage(page);
    setNotice(null);
    return true;
  }

  function savePhoto(file: File, options?: AddPageOptions) {
    const id = addPageFromBlob(scriptId, file, options);
    setThumbs(prev => ({ ...prev, [id]: URL.createObjectURL(file) }));
    setNotice(null);
  }

  /** Drops the old row and re-captures into the same slot, so the retaken
   * shot keeps its page number instead of landing at the end of the script.
   * `replaceRemote` is unconditional: the scanner just re-shot this page, so
   * whatever the server holds under this index — an already-uploaded photo,
   * or the copy that blocked an earlier attempt with `remote-mismatch` — is
   * by definition the stale one. */
  async function retakePage(page: Page, file: File) {
    if (!(await removePage(page))) return;
    savePhoto(file, { pageIndex: page.pageIndex, replaceRemote: true });
  }

  /** Setting `notice` and navigating in the same tick would never render —
   * this component unmounts immediately on `onBack()`. So the first tap
   * with an unresolved blocked page surfaces the notice and stays put;
   * a second tap (notice already showing) proceeds. */
  function handleBack() {
    const blockedNotice = blockedPageNotice(pages);
    if (blockedNotice && notice !== blockedNotice) {
      setNotice(blockedNotice);
      return;
    }
    onBack();
  }

  async function removeScript() {
    if (!window.confirm("Remove this script and its pages from this device?")) return;
    for (const thumb of Object.values(thumbs)) URL.revokeObjectURL(thumb);
    await deleteScriptAndPages(scriptId);
    onBack();
  }

  return (
    <If when={script}>
      {script => (
        <>
          <div
            className="flex min-h-0 flex-1 flex-col p-6"
            style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 5.5rem)" }}
          >
            <div className="mb-2 flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={handleBack}>
                <LuArrowLeft />
                Back
              </Button>
              <Heading as="h1" variant="h4" className="min-w-0 flex-1 truncate text-lg">
                {displayLabel(script)}
              </Heading>
              <Button variant="ghost" size="icon-sm" onClick={openEdit} aria-label="Edit script">
                <LuPencil />
              </Button>
              <Button variant="ghost" size="icon-sm" onClick={() => void removeScript()} aria-label="Remove script">
                <LuTrash2 />
              </Button>
            </div>

            <MetaInfo script={script} pages={pages} uploadedCount={uploadedCount} />

            <If when={flag?.message ? flag : null}>
              {f => (
                <Alert variant="warning" className="mb-3">
                  <LuFlag />
                  <AlertTitle>{f.ackedAt ? "Marked resolved" : "Flagged by reviewer"}</AlertTitle>
                  <AlertDescription>
                    {f.message}
                    <If condition={Boolean(f.ackedAt)}>
                      <>
                        <br />
                        {f.reply ? `Note: ${f.reply}` : "No note"}
                      </>
                    </If>
                  </AlertDescription>
                  <AlertAction>
                    <Button
                      size="sm"
                      variant="warning"
                      disabled={pending || !script.remoteId}
                      onClick={() => setResolveOpen(true)}
                    >
                      {f.ackedAt ? "Update note" : "Mark resolved"}
                    </Button>
                  </AlertAction>
                </Alert>
              )}
            </If>

            <ResolveFlagDialog
              open={resolveOpen}
              onOpenChange={setResolveOpen}
              onSubmit={reply => void handleResolve(reply)}
              submitting={pending}
              initialReply={flag?.reply ?? ""}
            />

            <If when={banner}>
              {text => (
                <div className="mb-3 rounded-md bg-muted px-3 py-2">
                  <Small>{text}</Small>
                </div>
              )}
            </If>

            <If when={notice}>{msg => <Small className="mb-3 block text-destructive">{msg}</Small>}</If>

            <UploadNowButton pages={pages} className="mb-3" />

            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              {pages.map(page => (
                <div key={page.id} className="overflow-hidden rounded-md border">
                  <If when={thumbs[page.id]}>
                    {src => (
                      <button
                        type="button"
                        className="block w-full cursor-pointer"
                        onClick={() => setLightboxPageId(page.id)}
                        aria-label={`View page ${page.pageIndex} enlarged`}
                      >
                        <img src={src} alt={`Page ${page.pageIndex}`} className="w-full object-contain" />
                      </button>
                    )}
                  </If>
                  <div className="flex items-center justify-between px-2 py-1 text-xs">
                    <span>#{page.pageIndex}</span>
                    <span
                      className={
                        page.uploadStatus === "blocked"
                          ? "text-destructive"
                          : page.uploadStatus === "uploaded"
                            ? "text-muted-foreground"
                            : "text-primary"
                      }
                    >
                      {pageStatusLabel(page)}
                    </span>
                  </div>
                  <If when={failReasonLabel(page)}>
                    {reason => (
                      <p className="border-t px-2 py-1 text-[11px] leading-snug text-muted-foreground">{reason}</p>
                    )}
                  </If>
                  <div className="flex gap-1 border-t px-1.5 py-1.5">
                    <NativeCameraButton
                      variant="ghost"
                      size="xs"
                      disabled={!isRemovable(page.uploadStatus)}
                      onPhoto={file => void retakePage(page, file)}
                    >
                      <LuRotateCcw />
                      Retake
                    </NativeCameraButton>
                    <Button
                      variant="ghost"
                      size="xs"
                      disabled={!isRemovable(page.uploadStatus)}
                      onClick={() => void removePage(page)}
                    >
                      <LuTrash2 />
                      Remove
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            <If condition={editing}>
              <ScriptFormDrawer script={script} onClose={closeEdit} onSaved={closeEdit} />
            </If>
          </div>

          <div
            className="fixed inset-x-0 bottom-0 z-20 border-t bg-background/95 backdrop-blur-sm"
            style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
          >
            <div className="mx-auto max-w-md px-6 py-3">
              <NativeCameraButton className="h-16 w-full text-base" onPhoto={file => void savePhoto(file)}>
                <LuCamera />
                Take photo
              </NativeCameraButton>
            </div>
          </div>

          <Lightbox
            open={lightboxPage !== null}
            onOpenChange={open => {
              if (!open) setLightboxPageId(null);
            }}
            src={lightboxPage ? (thumbs[lightboxPage.id] ?? null) : null}
            alt={lightboxPage ? `Page ${lightboxPage.pageIndex}` : ""}
            action={
              // Enlarged is where a blurry page actually gets noticed, so the
              // fix belongs here too — retaking closes the lightbox, since the
              // row it was showing is gone.
              lightboxPage && isRemovable(lightboxPage.uploadStatus) ? (
                <NativeCameraButton
                  size="xl"
                  onPhoto={file => void retakePage(lightboxPage, file)}
                >
                  <LuRotateCcw />
                  Retake page {lightboxPage.pageIndex}
                </NativeCameraButton>
              ) : undefined
            }
          />
        </>
      )}
    </If>
  );
}

function MetaInfo({
  script,
  pages,
  uploadedCount,
}: {
  script: Script;
  pages: Page[];
  uploadedCount: number;
}) {
  const pageCount = pages.length;
  const fields: Array<[string, string]> = [
    ...(script.rollNumber ? [["Roll", script.rollNumber] as [string, string]] : []),
    ...(script.studentName ? [["Name", script.studentName] as [string, string]] : []),
    ...(script.gender ? [["Gender", script.gender === "male" ? "Male" : "Female"] as [string, string]] : []),
    ...(script.subject ? [["Subject", script.subject] as [string, string]] : []),
    ...(script.section ? [["Section", script.section] as [string, string]] : []),
  ];

  return (
    <div className="mb-4 rounded-md border bg-muted/40 px-3 py-2 text-sm">
      <If condition={fields.length > 0}>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-1">
          {fields.map(([label, value]) => (
            <div key={label} className="contents">
              <dt>
                <Muted as="span">{label}</Muted>
              </dt>
              <dd className="truncate">
                <Small>{value}</Small>
              </dd>
            </div>
          ))}
        </dl>
        <Else>
          <Muted>No details captured</Muted>
        </Else>
      </If>
      <If condition={pageCount > 0}>
        <div className="mt-2 space-y-1">
          <Progress value={uploadedCount} max={pageCount} />
          <Muted className="text-xs">{pageStatusBreakdown(pages)}</Muted>
        </div>
      </If>
    </div>
  );
}
